import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import ProductApi from "../Api/ProductApi";
import usePermissions from "../hook/usePermissions";
import ImageUploadApi from "../Api/ImageUploadApi";
import { IMAGE_BASE_URL } from "../Config/Index";
import CategoryApi from "../Api/CategoryApi";
import SubCategoryApi from "../Api/SubCategoryApi";
import BrandApi from "../Api/BrandApi";
import UnitApi from "../Api/UnitApi";
import TaxApi from "../Api/TaxApi";
import AttributeApi from "../Api/AttributeApi";
import ChildCategoryApi from "../Api/ChildCategoryApi";
import ImageCropper from "./ImageCropper";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { identifyAverageColor } from "../helper/ColorIdentifier";

const generateSku = (productName, combination) => {
  if (!productName) return "";
  const base = productName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with dashes
    .replace(/^-+|-+$/g, '');   // Trim dashes

  const suffix = combination
    ?.map(c => c.value)
    ?.filter(Boolean)
    ?.map(v => v.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-'))
    ?.join('-');

  return suffix ? `${base}-${suffix}`.toUpperCase() : base.toUpperCase();
};

const validateImageDimensions = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        if (img.width !== 1200 || img.height !== 1600) {
          resolve({ valid: false, width: img.width, height: img.height });
        } else {
          resolve({ valid: true });
        }
      };
      img.onerror = () => resolve({ valid: false, width: 0, height: 0 });
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

const ProductFormLayer = () => {
  const { hasPermission } = usePermissions();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isView = location.pathname.includes("/view/");

  const [formData, setFormData] = useState({
    "name": "",
    "slug": "",
    "categoryId": "",
    "subCategoryId": "",
    "childCategoryId": "",
    "brandId": "",
    "unitId": "",
    "taxId": "",
    "hsnCode": "",
    "weight": "",
    "shortDescription": "",
    "fullDescription": "",
    "refundable": false,
    "status": false,
    "isFeatured": false,
    "isNewArrival": false,
    "lowStockAlert": "",
    "metaTitle": "",
    "metaKeywords": "",
    "metaDescription": "",
    "estimatedDeliveryDays": "",
    "youtubeLink": "",
    "isSingleImage": true,
    "attributes": [
      {
        "combination": [
          {
            "attributeId": "",
            "value": "",
            "valueId": ""
          }
        ],
        "sku": "",
        "stock": "",
        "minOrderQty": "",
        "maxOrderQty": "",
        "mrp": "",
        "price": "",
        "images": []
      }
    ],
    "specifications": [
      {
        "key": "",
        "value": ""
      }
    ]
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [attributeValueModal, setAttributeValueModal] = useState({ show: false, attributeId: null, name: "", image: null, preview: null, blob: null });
  const [cropperState, setCropperState] = useState({ show: false, image: null });
  const [expandedVariations, setExpandedVariations] = useState({ 0: true });

  const [selectedAttributes, setSelectedAttributes] = useState([
    { attributeId: "", values: [] }
  ]);
  const [isAttributesInitialized, setIsAttributesInitialized] = useState(false);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ show: false, index: null, type: "" });

  // Assuming a generic ReactQuill for text editor
  const [fullDescriptionContent, setFullDescriptionContent] = useState("");
  const [attributeOptions, setAttributeOptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [childCategories, setChildCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);

  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  const booleanOptions = [
    { value: true, label: "Yes" },
    { value: false, label: "No" },
  ];

  const getCartesianProduct = (attributesList) => {
    const activeAttrs = attributesList.filter(a => a.attributeId && a.values && a.values.length > 0);
    if (activeAttrs.length === 0) return [];

    const result = [];
    const helper = (depth, currentCombination) => {
      if (depth === activeAttrs.length) {
        result.push([...currentCombination]);
        return;
      }
      const currentAttr = activeAttrs[depth];
      currentAttr.values.forEach(val => {
        helper(depth + 1, [
          ...currentCombination,
          {
            attributeId: currentAttr.attributeId,
            value: val.value || val.name,
            valueId: val.id || val.valueId || ""
          }
        ]);
      });
    };

    helper(0, []);
    return result;
  };

  const areCombinationsEqual = (combA, combB) => {
    if (!combA || !combB) return false;
    if (combA.length !== combB.length) return false;
    return combA.every(itemA =>
      combB.some(itemB =>
        itemB.attributeId === itemA.attributeId &&
        (itemB.valueId === itemA.valueId || itemB.value === itemA.value)
      )
    );
  };

  const syncVariants = (currentAttributes, newCombinations, productName) => {
    const synced = newCombinations.map(comb => {
      const existing = currentAttributes.find(attr => areCombinationsEqual(attr.combination, comb));
      if (existing) {
        return existing;
      } else {
        const randomPrefix = Math.floor(1000 + Math.random() * 9000);
        const generatedSkuValue = generateSku(productName || formData.name || "prod", comb);
        return {
          combination: comb,
          sku: generatedSkuValue,
          stock: 0,
          minOrderQty: 0,
          maxOrderQty: 0,
          mrp: "",
          price: "",
          images: currentAttributes.length > 0 ? [...(currentAttributes[0].images || [])] : [],
          _tempId: randomPrefix
        };
      }
    });
    return synced;
  };

  const handleSelectedAttributesChange = (index, field, value) => {
    setSelectedAttributes(prev => {
      const updated = [...prev];
      if (field === "attributeId") {
        updated[index] = {
          attributeId: value,
          values: []
        };
      } else if (field === "values") {
        updated[index] = {
          ...updated[index],
          values: value || []
        };
      }

      const newCombinations = getCartesianProduct(updated);
      setFormData(prevForm => {
        const synced = syncVariants(prevForm.attributes, newCombinations, prevForm.name);
        return {
          ...prevForm,
          attributes: synced
        };
      });

      return updated;
    });
  };

  const handleAddSelectedAttributeRow = () => {
    setSelectedAttributes(prev => [...prev, { attributeId: "", values: [] }]);
  };

  const handleRemoveSelectedAttributeRow = (index) => {
    setSelectedAttributes(prev => {
      const updated = prev.filter((_, i) => i !== index);
      const newCombinations = getCartesianProduct(updated);
      setFormData(prevForm => {
        const synced = syncVariants(prevForm.attributes, newCombinations, prevForm.name);
        return {
          ...prevForm,
          attributes: synced
        };
      });
      return updated;
    });
  };

  useEffect(() => {
    if (id && formData.attributes && formData.attributes.length > 0 && attributeOptions.length > 0 && !isAttributesInitialized) {
      const attrMap = {};
      formData.attributes.forEach(attr => {
        if (attr.combination) {
          attr.combination.forEach(c => {
            if (c.attributeId) {
              if (!attrMap[c.attributeId]) {
                attrMap[c.attributeId] = {};
              }
              const key = c.valueId || c.value;
              attrMap[c.attributeId][key] = {
                id: c.valueId || "",
                name: c.value || "",
                value: c.value || "",
                label: c.value || ""
              };
            }
          });
        }
      });

      const rebuilt = Object.keys(attrMap).map(attrId => {
        const valuesArray = Object.values(attrMap[attrId]);
        return {
          attributeId: attrId,
          values: valuesArray
        };
      });

      if (rebuilt.length > 0) {
        setSelectedAttributes(rebuilt);
        setIsAttributesInitialized(true);
      }
    }
  }, [formData.attributes, attributeOptions, id, isAttributesInitialized]);

  useEffect(() => {
    if (formData.attributes) {
      const initialExpanded = {};
      formData.attributes.forEach((_, idx) => {
        initialExpanded[idx] = true;
      });
      setExpandedVariations(prev => ({ ...initialExpanded, ...prev }));
    }
  }, [formData.attributes?.length]);

  useEffect(() => {
    fetchCategories();
    fetchSubCategories();
    fetchChildCategories();
    fetchBrands();
    fetchUnits();
    fetchTaxes();
    fetchAttributes();
    if (id) fetchDetails();
  }, [id]);

  const fetchCategories = async () => {
    const res = await CategoryApi.getAll(0, 1000, "", true);
    if (res.status && res.response.data) setCategories(res.response.data.map(c => ({ value: c._id, label: c.name })));
  };

  const fetchSubCategories = async () => {
    const res = await SubCategoryApi.getAll(0, 1000, "", true, true);
    if (res.status && res.response.data) {
      setSubCategories(res.response.data); // Store raw data to access categoryId
    }
  };

  const fetchChildCategories = async () => {
    const res = await ChildCategoryApi.getAll(0, 1000, "", true, true);
    if (res.status && res.response.data) {
      setChildCategories(res.response.data); // Store raw data to access subCategoryId
    }
  };

  const fetchBrands = async () => {
    const res = await BrandApi.getAll(0, 1000, "");
    if (res.status && res.response.data) setBrands(res.response.data.map(c => ({ value: c._id, label: c.name })));
  };

  const fetchUnits = async () => {
    const res = await UnitApi.getAll(0, 1000, "");
    if (res.status && res.response.data) setUnits(res.response.data.map(c => ({ value: c._id, label: c.name })));
  };

  const fetchTaxes = async () => {
    const res = await TaxApi.getAll(0, 1000, "");
    if (res.status && res.response.data) {
      const options = res.response.data.map(c => ({ value: c._id, label: c.name }));
      setTaxes(options);

      // Default the tax selection to "GST" for a new product
      if (!id) {
        const gstTax = options.find(opt => opt.label.toUpperCase() === "GST");
        if (gstTax) {
          setFormData(prev => ({ ...prev, taxId: gstTax.value }));
        }
      }
    }
  };

  const fetchAttributes = async () => {
    const res = await AttributeApi.getAll(0, 1000, "");
    if (res.status && res.response.data) {
      const options = res.response.data.map(attr => ({
        value: attr._id,
        label: attr.name,
        values: attr.values || []
      }));
      setAttributeOptions(options);

      // Default the first attribute combination to "Size" and "S" for a new product
      if (!id) {
        const sizeAttr = options.find(opt => opt.label.toLowerCase() === "size");
        if (sizeAttr) {
          const sVal = sizeAttr.values.find(v => v.name.toUpperCase() === "S");
          const defaultComb = sVal ? [{
            attributeId: sizeAttr.value,
            value: sVal.name,
            valueId: sVal._id || sVal.id || ""
          }] : [];

          setSelectedAttributes([
            {
              attributeId: sizeAttr.value,
              values: sVal ? [{
                id: sVal._id || sVal.id || "",
                name: sVal.name,
                value: sVal.name,
                label: sVal.name
              }] : []
            }
          ]);

          setFormData(prev => ({
            ...prev,
            attributes: [{
              combination: defaultComb,
              sku: generateSku(prev.name, defaultComb),
              stock: 0,
              minOrderQty: 0,
              maxOrderQty: 0,
              mrp: "",
              price: "",
              images: [],
              _tempId: Math.floor(1000 + Math.random() * 9000)
            }]
          }));
        }
      }
    }
  };

  const fetchDetails = async () => {
    setLoading(true);
    const response = await ProductApi.getById(id);
    if (response.status) {
      const data = response.response.data || response.response;
      setFormData(prev => ({
        ...prev,
        ...data,
        status: data.status !== undefined ? (data.status === 1 || data.status === "active" || data.status === true) : data.isActive !== undefined ? data.isActive : true
      }));
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "name") {
      const generatedSlug = value.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '');
      setFormData(prev => {
        const updatedAttributes = prev.attributes.map(attr => ({
          ...attr,
          sku: generateSku(value, attr.combination)
        }));
        return {
          ...prev,
          name: value,
          slug: generatedSlug,
          attributes: updatedAttributes
        };
      });
      if (errors.name) setErrors(prev => ({ ...prev, name: "" }));
      if (errors.slug) setErrors(prev => ({ ...prev, slug: "" }));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
      if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleKeyDown = (e) => {
    // Block signs like + and - as well as 'e' for scientific notation
    if (["e", "E", "+", "-"].includes(e.key)) {
      e.preventDefault();
    }
  };



  const handleNewAttributeFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Image size must be less than 2MB");
        e.target.value = "";
        return;
      }
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setCropperState({ show: true, image: reader.result });
      });
      reader.readAsDataURL(file);
    }
  };

  const onNewValueCropComplete = async (cropped) => {
    let colorName = attributeValueModal.name;
    if (!colorName || colorName.trim() === "") {
      const color = await identifyAverageColor(cropped.url);
      colorName = color.name;
    }
    setAttributeValueModal(prev => ({ ...prev, blob: cropped.blob, preview: cropped.url, name: colorName }));
    setCropperState({ show: false, image: null });
  };

  const saveNewAttributeValue = async () => {
    if (!attributeValueModal.name?.trim()) return;

    setLoading(true);
    let finalImage = null;
    if (attributeValueModal.blob) {
      const formDataUpload = new FormData();
      formDataUpload.append("file", attributeValueModal.blob, "swatch.jpg");
      const uploadRes = await ImageUploadApi.uploadImage({ formData: formDataUpload, path: "attributes" });
      if (uploadRes.status) {
        finalImage = uploadRes.response.data || uploadRes.response;
      }
    }

    const attrRes = await AttributeApi.getById(attributeValueModal.attributeId);
    if (attrRes.status) {
      const attrData = attrRes.response.data || attrRes.response;
      const updatedValues = [...(attrData.values || []), { name: attributeValueModal.name, image: finalImage }];

      const updateRes = await AttributeApi.update(attributeValueModal.attributeId, { ...attrData, values: updatedValues });
      if (updateRes.status) {
        await fetchAttributes(); // Refresh options
        setAttributeValueModal({ show: false, attributeId: null, name: "", image: null, preview: null, blob: null });
      }
    }
    setLoading(false);
  };

  const handleVariationImageUpload = async (e, index) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    const invalidFiles = [];
    for (const file of files) {
      if (file.size > 2 * 1024 * 1024) {
        invalidFiles.push(file.name);
      } else {
        validFiles.push(file);
      }
    }

    if (invalidFiles.length > 0) {
      toast.error(`Some files exceed the 2MB limit: ${invalidFiles.join(", ")}`);
    }

    if (validFiles.length > 0) {
      setLoading(true);
      const updated = [...formData.attributes];
      if (!updated[index].images) updated[index].images = [];
      const uploadedImages = [];
      for (const file of validFiles) {
        const form = new FormData();
        form.append("file", file);
        const uploadRes = await ImageUploadApi.uploadImage({ formData: form, path: "products" });
        if (uploadRes.status) {
          uploadedImages.push(uploadRes.response.data || uploadRes.response);
        }
      }
      if (formData.isSingleImage) {
        updated.forEach(attr => {
          if (!attr.images) attr.images = [];
          attr.images.push(...uploadedImages);
        });
        setErrors(prev => {
          const nextErrors = { ...prev };
          delete nextErrors.images;
          return nextErrors;
        });
      } else {
        updated[index].images.push(...uploadedImages);
        setErrors(prev => {
          const nextErrors = { ...prev };
          delete nextErrors[`variation_${index}_images`];
          return nextErrors;
        });
      }
      setFormData(prev => ({ ...prev, attributes: updated }));
      setLoading(false);
    }
    e.target.value = ""; // Clear file input
  };

  const handleTextEditorChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (selectedOption, { name }) => {
    setFormData(prev => ({ ...prev, [name]: selectedOption ? selectedOption.value : (selectedOption === false ? false : "") }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleAddSpecification = () => {
    setFormData(prev => ({ ...prev, specifications: [...prev.specifications, { key: "", value: "" }] }));
  };
  const handleRemoveSpecification = (index) => {
    setDeleteModal({ show: true, index, type: "specification" });
  };
  const handleSpecificationChange = (index, field, value) => {
    const updated = [...formData.specifications];
    updated[index][field] = value;
    setFormData(prev => ({ ...prev, specifications: updated }));
  };

  const handleAddAttribute = () => {
    const newIndex = formData.attributes.length;
    const sharedImages = (formData.isSingleImage && formData.attributes.length > 0)
      ? [...(formData.attributes[0].images || [])]
      : [];

    const sizeAttr = attributeOptions.find(opt => opt.label.toLowerCase() === "size");
    const defaultAttrId = sizeAttr ? sizeAttr.value : "";
    const sVal = sizeAttr?.values?.find(v => v.name.toUpperCase() === "S");

    const combination = [{
      attributeId: defaultAttrId,
      value: sVal ? sVal.name : "",
      valueId: sVal ? (sVal._id || sVal.id || "") : ""
    }];
    const sku = generateSku(formData.name, combination);

    setFormData(prev => ({
      ...prev,
      attributes: [...prev.attributes, {
        combination,
        sku,
        stock: "",
        minOrderQty: "",
        maxOrderQty: "",
        mrp: "",
        price: "",
        images: sharedImages
      }]
    }));
    // Auto-expand the new one
    setExpandedVariations(prev => ({ ...prev, [newIndex]: true }));
  };

  const toggleVariation = (index) => {
    setExpandedVariations(prev => ({ ...prev, [index]: !prev[index] }));
  };
  const handleRemoveAttribute = (index) => {
    setDeleteModal({ show: true, index, type: "variation" });
  };

  const confirmDelete = () => {
    const { index, type } = deleteModal;
    if (index !== null) {
      if (type === "variation") {
        const removedVariant = formData.attributes[index];
        
        // 1. Update selectedAttributes by filtering out values matching the removed variant
        let newSelectedAttributes = [...selectedAttributes];
        if (removedVariant && removedVariant.combination) {
          newSelectedAttributes = selectedAttributes.map(row => {
            const match = removedVariant.combination.find(c => c.attributeId === row.attributeId);
            if (match) {
              const updatedValues = row.values.filter(val => {
                const valName = val.value || val.name || val.label;
                const valId = val.id || val.valueId;
                const matchName = match.value;
                const matchId = match.valueId;
                
                if (valId && matchId) {
                  return valId !== matchId;
                }
                return valName !== matchName;
              });
              return {
                ...row,
                values: updatedValues
              };
            }
            return row;
          });
          setSelectedAttributes(newSelectedAttributes);
        }

        // 2. Recalculate Cartesian product based on newSelectedAttributes
        const newCombinations = getCartesianProduct(newSelectedAttributes);
        
        // 3. Sync variants using the updated combinations
        setFormData(prevForm => {
          const synced = syncVariants(prevForm.attributes, newCombinations, prevForm.name);
          return {
            ...prevForm,
            attributes: synced
          };
        });

        toast.success("Product variation deleted successfully.");
      } else if (type === "specification") {
        const updated = [...formData.specifications];
        updated.splice(index, 1);
        setFormData(prev => ({ ...prev, specifications: updated }));
        toast.success("Product specification deleted successfully.");
      }
      setDeleteModal({ show: false, index: null, type: "" });
    }
  };

  const handleCloneAttribute = (index) => {
    const attributeToClone = formData.attributes[index];
    const cloned = JSON.parse(JSON.stringify(attributeToClone));
    cloned.sku = cloned.sku ? `${cloned.sku}-CLONE` : "";

    setFormData(prev => ({
      ...prev,
      attributes: [...prev.attributes, cloned]
    }));

    // Auto-expand the clone
    const newIdx = formData.attributes.length;
    setExpandedVariations(prev => ({ ...prev, [newIdx]: true }));
    toast.success(`Variation #${index + 1} cloned successfully.`);
  };

  const handleAttributeChange = (index, field, value) => {
    const updated = [...formData.attributes];
    updated[index][field] = value;
    setFormData(prev => ({ ...prev, attributes: updated }));
    
    const errorKey = `variation_${index}_${field}`;
    setErrors(prev => {
      if (prev[errorKey]) {
        const nextErrors = { ...prev };
        delete nextErrors[errorKey];
        return nextErrors;
      }
      return prev;
    });
  };

  const handleAddCombination = (index) => {
    const updated = [...formData.attributes];
    updated[index].combination.push({ attributeId: "", value: "", valueId: "" });
    setFormData(prev => ({ ...prev, attributes: updated }));
  };

  const handleRemoveCombination = (attrIndex, combIndex) => {
    const updated = [...formData.attributes];
    updated[attrIndex].combination.splice(combIndex, 1);
    setFormData(prev => ({ ...prev, attributes: updated }));
  };

  const handleCombinationChange = (attrIndex, combIndex, field, value) => {
    const updated = [...formData.attributes];
    updated[attrIndex].combination[combIndex][field] = value;
    if (field === "value" || field === "valueId") {
      updated[attrIndex].sku = generateSku(formData.name, updated[attrIndex].combination);
    }
    setFormData(prev => ({ ...prev, attributes: updated }));
  };
  const handleAttributeImageUpload = async (e, index) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    const invalidFiles = [];
    for (const file of files) {
      if (file.size > 2 * 1024 * 1024) {
        invalidFiles.push(file.name);
      } else {
        validFiles.push(file);
      }
    }

    if (invalidFiles.length > 0) {
      toast.error(`Some files exceed the 2MB limit: ${invalidFiles.join(", ")}`);
    }

    if (validFiles.length > 0) {
      setLoading(true);
      const updated = [...formData.attributes];
      for (const file of validFiles) {
        const form = new FormData();
        form.append("file", file);
        const uploadRes = await ImageUploadApi.uploadImage({ formData: form, path: "products" });
        if (uploadRes.status) updated[index].images.push(uploadRes.response.data || uploadRes.response);
      }
      setFormData(prev => ({ ...prev, attributes: updated }));
      setLoading(false);
    }
    e.target.value = ""; // Clear file input
  };
  const handleRemoveAttributeImage = async (attrIndex, imgIndex) => {
    const updated = [...formData.attributes];
    if (formData.isSingleImage) {
      updated.forEach(attr => {
        if (attr.images) {
          attr.images.splice(imgIndex, 1);
        }
      });
    } else {
      updated[attrIndex].images.splice(imgIndex, 1);
    }
    setFormData(prev => ({ ...prev, attributes: updated }));
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = "Product Name is Required.";
    if (!formData.slug?.trim()) newErrors.slug = "Slug is Required.";
    if (!formData.categoryId) newErrors.categoryId = "Category is Required.";
    if (!formData.taxId) newErrors.taxId = "Tax is Required.";
    if (formData.status === null || formData.status === undefined) newErrors.status = "Status is Required.";

    if (formData.isSingleImage) {
      if (!formData.attributes || formData.attributes.length === 0 || !formData.attributes[0]?.images || formData.attributes[0].images.length === 0) {
        newErrors.images = "Product Image is Required.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    let isValid = true;
    if (!formData.attributes || formData.attributes.length === 0) {
      newErrors.attributes = "At least one product variation is required.";
      isValid = false;
    } else {
      formData.attributes.forEach((attr, idx) => {
        const combComplete = attr.combination &&
          attr.combination.length > 0 &&
          attr.combination.every(c => c.attributeId && (c.value || c.valueId));

        if (!combComplete) {
          newErrors[`variation_${idx}_combination`] = "Please select attribute combination.";
          isValid = false;
        }

        if (!attr.sku || !attr.sku.trim()) {
          newErrors[`variation_${idx}_sku`] = "SKU is Required.";
          isValid = false;
        }

        if (!attr.price || parseFloat(attr.price) <= 0) {
          newErrors[`variation_${idx}_price`] = "Regular Price must be greater than 0.";
          isValid = false;
        }

        if (attr.stock === "" || attr.stock === null || attr.stock === undefined || parseFloat(attr.stock) < 0) {
          newErrors[`variation_${idx}_stock`] = "Stock is Required.";
          isValid = false;
        }

        if (!formData.isSingleImage) {
          if (!attr.images || attr.images.length === 0) {
            newErrors[`variation_${idx}_images`] = "At least one variation image is required.";
            isValid = false;
          }
        }
      });
    }

    setErrors(prev => {
      const nextErrors = { ...prev };
      Object.keys(nextErrors).forEach(key => {
        if (key.startsWith("variation_") || key === "attributes") {
          delete nextErrors[key];
        }
      });
      return { ...nextErrors, ...newErrors };
    });
    return isValid;
  };

  const validateForm = () => {
    const isStep1Valid = validateStep1();
    const isStep2Valid = validateStep2();
    return isStep1Valid && isStep2Valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isStep1Valid = validateStep1();
    const isStep2Valid = validateStep2();

    if (!isStep1Valid) {
      setCurrentStep(1);
      return;
    }
    if (!isStep2Valid) {
      setCurrentStep(2);
      return;
    }
    setLoading(true);
    let payload = { ...formData };
    payload.weight = payload.weight ? parseFloat(payload.weight) : null;
    payload.estimatedDeliveryDays = payload.estimatedDeliveryDays ? parseFloat(payload.estimatedDeliveryDays) : null;
    payload.lowStockAlert = payload.lowStockAlert ? parseFloat(payload.lowStockAlert) : null;
    if (payload.attributes && Array.isArray(payload.attributes)) {
      payload.attributes = payload.attributes
        .filter(attr => attr.combination && attr.combination.some(c => c.attributeId)) // Only keep variations with at least one attribute set
        .map(attr => ({
          combination: attr.combination.filter(c => c.attributeId).map(c => {
            let finalValueId = c.valueId;
            if (!finalValueId && c.value && c.attributeId) {
              const selectedAttr = attributeOptions.find(opt => opt.value === c.attributeId);
              const foundVal = selectedAttr?.values?.find(v => v.name === c.value);
              if (foundVal) {
                finalValueId = foundVal._id || foundVal.id;
              }
            }
            return {
              attributeId: c.attributeId,
              value: c.value,
              valueId: finalValueId
            };
          }),
          sku: attr.sku ? String(attr.sku) : "",
          stock: attr.stock ? parseFloat(attr.stock) : 0,
          minOrderQty: attr.minOrderQty ? parseFloat(attr.minOrderQty) : 0,
          maxOrderQty: attr.maxOrderQty ? parseFloat(attr.maxOrderQty) : 0,
          mrp: attr.mrp ? parseFloat(attr.mrp) : 0,
          price: attr.price ? parseFloat(attr.price) : 0,
          images: attr.images || []
        }));
    }
    payload.isActive = payload.status;
    let result;
    if (id) result = await ProductApi.update(id, payload);
    else result = await ProductApi.create(payload);
    setLoading(false);
    if (result.status) navigate("/products");
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24">
        <h6 className="text-primary-600 pb-2 mb-0">{isView ? "View" : id ? "Edit" : "Create"} Products</h6>
      </div>
      <div className="card-body p-24">
        <form className="form-wizard" onSubmit={handleSubmit} noValidate>
          {/* Wizard Header Stepper */}
          <div className='form-wizard-header overflow-x-auto scroll-sm pb-8 my-32'>
            <ul className='list-unstyled form-wizard-list' style={{ minWidth: 'auto', justifyContent: 'space-around' }}>
              <li
                className={`form-wizard-list__item text-center ${currentStep > 1 ? "activated" : ""} ${currentStep === 1 ? "active" : ""} `}
                style={{ width: '50%', cursor: 'pointer' }}
                onClick={() => {
                  if (currentStep > 1 || isView) {
                    setCurrentStep(1);
                  }
                }}
              >
                <div className='form-wizard-list__line'>
                  <span className='count d-inline-flex align-items-center justify-content-center'>
                    <Icon icon="lucide:edit-3" />
                  </span>
                </div>
                <span className='text text-xs fw-semibold'>Product</span>
              </li>
              <li
                className={`form-wizard-list__item text-center ${currentStep === 2 ? "active" : ""} `}
                style={{ width: '50%', cursor: 'pointer' }}
                onClick={() => {
                  if (isView) {
                    setCurrentStep(2);
                  } else if (currentStep === 1) {
                    if (validateStep1()) setCurrentStep(2);
                  }
                }}
              >
                <div className='form-wizard-list__line'>
                  <span className='count d-inline-flex align-items-center justify-content-center'>
                    <Icon icon="lucide:check" />
                  </span>
                </div>
                <span className='text text-xs fw-semibold'>Product Variant & Finish</span>
              </li>
            </ul>
          </div>
          <fieldset className={`wizard-fieldset ${currentStep === 1 ? "show" : ""}`}>
            <div className="row gy-4">
              {/* Product Name & Slug nested on the left */}
              <div className="col-md-6">
                <div className="row gy-4">
                  <div className="col-12">
                    <label className="form-label fw-semibold">Product Name <span className="text-danger">*</span></label>
                    <input type="text" className={`form-control radius-8 ${errors.name ? 'is-invalid' : ''}`} name="name" value={formData.name} onChange={handleChange} placeholder="Enter Product Name" disabled={isView} />
                    {errors.name && <small className="text-danger">{errors.name}</small>}
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">Slug <span className="text-danger">*</span></label>
                    <input type="text" className={`form-control radius-8 ${errors.slug ? 'is-invalid' : ''}`} name="slug" value={formData.slug} onChange={handleChange} placeholder="Enter Slug" disabled={isView} />
                    {errors.slug && <small className="text-danger">{errors.slug}</small>}
                  </div>
                </div>
              </div>

              {/* Checkbox and Image Uploader on the right */}
              <div className="col-md-6">
                <div className="d-flex align-items-center gap-2 mb-12">
                  <input
                    type="checkbox"
                    className="premium-checkbox"
                    id="isSingleImage"
                    name="isSingleImage"
                    checked={formData.isSingleImage || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData(prev => {
                        const updatedAttributes = prev.attributes.map(attr => ({
                          ...attr,
                          images: prev.attributes.length > 0 ? [...(prev.attributes[0].images || [])] : []
                        }));
                        return {
                          ...prev,
                          isSingleImage: checked,
                          attributes: updatedAttributes
                        };
                      });
                    }}
                    disabled={isView}
                  />
                  <label htmlFor="isSingleImage" className="form-check-label fw-semibold text-secondary-700 mb-0" style={{ cursor: "pointer" }}>
                    Use same images for all variants
                  </label>
                </div>

                {formData.isSingleImage && formData.attributes && formData.attributes.length > 0 ? (
                  <div className={`p-16 border dashed radius-12 bg-light-50 ${errors.images ? 'border-danger' : ''}`}>
                    <label className="form-label fw-bold mb-8 text-secondary-700">
                      Shared Variation Images (Applied to all variants) <span className="text-danger">*</span> <small className="text-secondary-light fw-normal">(Max 2MB)</small>
                    </label>
                    {!isView && (
                      <input
                        type="file"
                        multiple
                        className="form-control radius-8 mb-12"
                        accept="image/*"
                        onChange={(e) => handleVariationImageUpload(e, 0)}
                        disabled={loading}
                      />
                    )}
                    <div className="d-flex flex-wrap gap-12" style={{ maxHeight: "150px", overflowY: "auto" }}>
                      {formData.attributes[0]?.images && formData.attributes[0].images.map((img, i) => (
                        <div key={i} className="position-relative border radius-8 bg-white p-2">
                          <img src={`${IMAGE_BASE_URL}/${img.path || img}`} alt="" className="radius-8" style={{ width: "80px", height: "80px", objectFit: "contain" }} />
                          {!isView && (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 p-1 radius-4"
                              onClick={() => handleRemoveAttributeImage(0, i)}
                              style={{ padding: "2px 5px", fontSize: "10px" }}
                            >
                              <Icon icon="fluent:delete-24-regular" style={{ fontSize: "12px" }} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  !formData.isSingleImage ? (
                    <div className="p-16 border radius-12 bg-light-50 text-secondary-light text-sm">
                      <Icon icon="solar:info-circle-bold" className="me-2 text-primary-600" />
                      Individual variation images can be uploaded for each variation block in Step 2.
                    </div>
                  ) : (
                    <div className="p-16 border radius-12 bg-light-50 text-secondary-light text-sm">
                      <Icon icon="solar:info-circle-bold" className="me-2 text-warning" />
                      No variation initialized yet. Please wait or check attributes in Step 2.
                    </div>
                  )
                )}
                {formData.isSingleImage && errors.images && <small className="text-danger mt-8 d-block">{errors.images}</small>}
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Category <span className="text-danger">*</span></label>
                <Select name="categoryId" options={categories} value={categories.find((opt) => opt.value === formData.categoryId)} onChange={handleSelectChange} styles={selectStyles(errors.categoryId)} isDisabled={isView} isClearable placeholder="Select Category" />
                {errors.categoryId && <small className="text-danger">{errors.categoryId}</small>}
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Sub Category</label>
                <Select
                  name="subCategoryId"
                  options={subCategories.filter(s => s.categoryId === formData.categoryId).map(s => ({ value: s._id, label: s.name }))}
                  value={subCategories.filter(s => s.categoryId === formData.categoryId).map(s => ({ value: s._id, label: s.name })).find((opt) => opt.value === formData.subCategoryId)}
                  onChange={(selectedOption, actionMeta) => {
                    handleSelectChange(selectedOption, actionMeta);
                    // Reset child category when sub category changes
                    setFormData(prev => ({ ...prev, childCategoryId: "" }));
                  }}
                  styles={selectStyles(errors.subCategoryId)}
                  isDisabled={isView || !formData.categoryId}
                  isClearable
                  placeholder={formData.categoryId ? "Select Sub Category" : "Please select category first"}
                />
                {errors.subCategoryId && <small className="text-danger">{errors.subCategoryId}</small>}
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Child Category</label>
                <Select
                  name="childCategoryId"
                  options={childCategories.filter(s => s.subCategoryId === formData.subCategoryId).map(s => ({ value: s._id, label: s.name }))}
                  value={childCategories.filter(s => s.subCategoryId === formData.subCategoryId).map(s => ({ value: s._id, label: s.name })).find((opt) => opt.value === formData.childCategoryId)}
                  onChange={handleSelectChange}
                  styles={selectStyles(errors.childCategoryId)}
                  isDisabled={isView || !formData.subCategoryId}
                  isClearable
                  placeholder={formData.subCategoryId ? "Select Child Category" : "Please select sub category first"}
                />
                {errors.childCategoryId && <small className="text-danger">{errors.childCategoryId}</small>}
              </div>
              {/* <div className="col-md-6">
              <label className="form-label fw-semibold">Brand <span className="text-danger">*</span></label>
              <Select name="brandId" options={brands} value={brands.find((opt) => opt.value === formData.brandId)} onChange={handleSelectChange} styles={selectStyles(errors.brandId)} isDisabled={isView} isClearable placeholder="Select Brand" />
              {errors.brandId && <small className="text-danger">{errors.brandId}</small>}
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Unit <span className="text-danger">*</span></label>
              <Select name="unitId" options={units} value={units.find((opt) => opt.value === formData.unitId)} onChange={handleSelectChange} styles={selectStyles(errors.unitId)} isDisabled={isView} isClearable placeholder="Select Unit" />
              {errors.unitId && <small className="text-danger">{errors.unitId}</small>}
            </div> */}
              <div className="col-md-6">
                <label className="form-label fw-semibold">Tax <span className="text-danger">*</span></label>
                <Select name="taxId" options={taxes} value={taxes.find((opt) => opt.value === formData.taxId)} onChange={handleSelectChange} styles={selectStyles(errors.taxId)} isDisabled={isView} isClearable placeholder="Select Tax" />
                {errors.taxId && <small className="text-danger">{errors.taxId}</small>}
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">HSN Code</label>
                <input type="text" className="form-control radius-8" name="hsnCode" value={formData.hsnCode} onChange={handleChange} placeholder="Enter HSN Code" disabled={isView} />
                {errors.hsnCode && <small className="text-danger">{errors.hsnCode}</small>}
              </div>
              {/* <div className="col-md-6">
              <label className="form-label fw-semibold">Weight</label>
              <input
                type="number"
                className="form-control radius-8"
                name="weight"
                value={formData.weight}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder="Enter Weight"
                disabled={isView}
                min="0"
              />
              {errors.weight && <small className="text-danger">{errors.weight}</small>}
            </div> */}
              <div className="col-md-6">
                <label className="form-label fw-semibold">No of Days Delivery</label>
                <input
                  type="number"
                  className="form-control radius-8"
                  name="estimatedDeliveryDays"
                  value={formData.estimatedDeliveryDays}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  onWheel={(e) => e.target.blur()}
                  placeholder="Enter Number of Days"
                  disabled={isView}
                  min="0"
                />
                {errors.estimatedDeliveryDays && <small className="text-danger">{errors.estimatedDeliveryDays}</small>}
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">YouTube Link</label>
                <input
                  type="text"
                  className="form-control radius-8"
                  name="youtubeLink"
                  value={formData.youtubeLink}
                  onChange={handleChange}
                  placeholder="Enter YouTube URL"
                  disabled={isView}
                />
                {errors.youtubeLink && <small className="text-danger">{errors.youtubeLink}</small>}
              </div>
              {/* <div className="col-md-12">
              <label className="form-label fw-semibold">Short Description</label>
              <textarea className="form-control radius-8" name="shortDescription" value={formData.shortDescription} onChange={handleChange} placeholder="Enter Short Description" disabled={isView} rows="3" />
            </div> */}
              <div className="col-md-12">
                <label className="form-label fw-semibold">Description</label>
                <textarea className="form-control radius-8" name="fullDescription" value={formData.fullDescription} onChange={handleTextEditorChange} placeholder="Enter Full Description (Simulated Text Editor)" disabled={isView} rows="6" />
              </div>
              {/* <div className="col-md-6">
              <label className="form-label fw-semibold">Refundable (yes/no) <span className="text-danger">*</span></label>
              <Select name="refundable" options={booleanOptions} value={booleanOptions.find((opt) => opt.value === formData.refundable)} onChange={handleSelectChange} styles={selectStyles(errors.refundable)} isDisabled={isView} isClearable={false} />
              {errors.refundable && <small className="text-danger">{errors.refundable}</small>}
            </div> */}
              <div className="col-md-6">
                <label className="form-label fw-semibold">Status <span className="text-danger">*</span></label>
                <Select name="status" options={statusOptions} value={statusOptions.find((opt) => opt.value === formData.status)} onChange={handleSelectChange} styles={selectStyles(errors.status)} isDisabled={isView} isClearable={false} />
                {errors.status && <small className="text-danger">{errors.status}</small>}
              </div>
              <div className="col-md-6 d-flex flex-wrap align-items-center gap-3 gap-sm-5 mb-0 mt-32">
                <div className="form-check d-flex align-items-center gap-2">
                  <input className="premium-checkbox" type="checkbox" name="isFeatured" checked={formData.isFeatured || false} onChange={handleChange} id="featuredSwitch" />
                  <label className="form-check-label fw-semibold text-secondary-700 mb-0" htmlFor="featuredSwitch">Featured Product</label>
                </div>
                <div className="form-check d-flex align-items-center gap-2">
                  <input className="premium-checkbox" type="checkbox" name="isNewArrival" checked={formData.isNewArrival || false} onChange={handleChange} id="newArrivalSwitch" />
                  <label className="form-check-label fw-semibold text-secondary-700 mb-0" htmlFor="newArrivalSwitch">New Arrival</label>
                </div>
              </div>
              {/* <div className="col-md-6">
              <label className="form-label fw-semibold">Low stock alert qnt <span className="text-danger">*</span></label>
              <input
                type="number"
                className="form-control radius-8"
                name="lowStockAlert"
                value={formData.lowStockAlert}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder="Enter Low stock alert qnt"
                disabled={isView}
                min="0"
              />
              {errors.lowStockAlert && <small className="text-danger">{errors.lowStockAlert}</small>}
            </div> */}
              <div className="col-md-6">
                <label className="form-label fw-semibold">Meta Title</label>
                <input type="text" className="form-control radius-8" name="metaTitle" value={formData.metaTitle} onChange={handleChange} placeholder="Enter Meta Title" disabled={isView} />
                {errors.metaTitle && <small className="text-danger">{errors.metaTitle}</small>}
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Meta Keywords</label>
                <input type="text" className="form-control radius-8" name="metaKeywords" value={formData.metaKeywords} onChange={handleChange} placeholder="Enter Meta Keywords" disabled={isView} />
                {errors.metaKeywords && <small className="text-danger">{errors.metaKeywords}</small>}
              </div>
              <div className="col-md-12">
                <label className="form-label fw-semibold">Meta Description</label>
                <textarea className="form-control radius-8" name="metaDescription" value={formData.metaDescription} onChange={handleChange} placeholder="Enter Meta Description" disabled={isView} rows="3" />
              </div>


            </div>
            {/* Form Actions for Step 1 */}
            <div className="d-flex justify-content-end gap-3 mt-40 pt-24 border-top">
              <Link to="/products" className="btn btn-outline-secondary px-32 py-12 radius-8 fw-semibold" style={{ minWidth: "140px" }}>
                Cancel
              </Link>
              <button
                type="button"
                className="btn btn-primary radius-8 px-32 py-12 fw-semibold d-flex align-items-center justify-content-center gap-2"
                style={{ backgroundColor: "#364E3D", borderColor: "#364E3D", minWidth: "140px" }}
                onClick={() => {
                  if (isView || validateStep1()) {
                    setCurrentStep(2);
                  }
                }}
              >
                <span>Next</span>
                <Icon icon="lucide:arrow-right" />
              </button>
            </div>
          </fieldset>

          <fieldset className={`wizard-fieldset ${currentStep === 2 ? "show" : ""}`}>
            {/* Product Variations & Specifications Section */}
            {/* Product Variations Section */}
            <div className="mt-5 pt-4 border-top">
              <div className="mb-4">
                <h6 className="mb-0 fw-bold text-lg">Product Variations (Combinations)</h6>
              </div>

              {errors.attributes && (
                <div className="alert alert-danger radius-8 p-16 mb-20 d-flex align-items-center gap-2" style={{ backgroundColor: "#fdf2f2", borderColor: "#fde8e8" }}>
                  <Icon icon="solar:info-circle-bold" fontSize="20px" className="text-danger" />
                  <span className="fw-semibold text-danger">{errors.attributes}</span>
                </div>
              )}

              {/* Top Multi-Attribute Selection Section */}
              <div className="mb-24 p-24 border radius-12 bg-light-50">
                <label className="form-label fw-bold mb-16 text-secondary-700">
                  Define Variation Attributes (e.g. Size, Color)
                </label>

                {selectedAttributes.map((row, rIdx) => {
                  const selectedAttr = attributeOptions.find(opt => opt.value === row.attributeId);
                  const valueOptions = selectedAttr?.values ? selectedAttr.values.map(v => ({
                    value: v.name,
                    label: v.name,
                    id: v._id || v.id || "",
                    image: v.image
                  })) : [];

                  return (
                    <div key={rIdx} className="row gy-3 mb-16 align-items-end bg-white p-16 radius-8 border">
                      <div className="col-md-5">
                        <label className="form-label text-xs fw-bold text-secondary-600 mb-8">Attribute Type :</label>
                        <Select
                          options={attributeOptions.filter(opt => !selectedAttributes.some((r, ri) => ri !== rIdx && r.attributeId === opt.value))}
                          value={attributeOptions.find(opt => opt.value === row.attributeId)}
                          onChange={(sel) => handleSelectedAttributesChange(rIdx, "attributeId", sel ? sel.value : "")}
                          isDisabled={isView}
                          styles={selectStyles()}
                          placeholder="Select Attribute Type"
                        />
                      </div>
                      <div className="col-md-5">
                        <label className="form-label text-xs fw-bold text-secondary-600 mb-8">Attribute Value :</label>
                        <Select
                          isMulti
                          options={valueOptions}
                          value={row.values}
                          onChange={(sel) => handleSelectedAttributesChange(rIdx, "values", sel)}
                          isDisabled={isView || !row.attributeId}
                          styles={selectStyles()}
                          placeholder="Select Attribute Values"
                          closeMenuOnSelect={false}
                        />
                      </div>
                      {!isView && (
                        <div className="col-md-2 d-flex justify-content-end mb-1">
                          <button
                            type="button"
                            className="btn btn-danger radius-8 px-20 py-10 d-flex align-items-center justify-content-center gap-2 text-white"
                            onClick={() => handleRemoveSelectedAttributeRow(rIdx)}
                            disabled={selectedAttributes.length === 1}
                            style={{ backgroundColor: "#FF4C61", borderColor: "#FF4C61" }}
                          >
                            <span>Remove</span>
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}

                {!isView && (
                  <button
                    type="button"
                    className="btn radius-8 px-20 py-10 d-flex align-items-center gap-2 mt-8 text-white"
                    onClick={handleAddSelectedAttributeRow}
                    style={{ backgroundColor: "#364E3D", borderColor: "#364E3D" }}
                  >
                    <Icon icon="ic:baseline-plus" fontSize="20px" />
                    <span>Add Variant</span>
                  </button>
                )}
              </div>

              {/* Image uploader moved to Step 1 */}

              {formData.attributes.map((attr, index) => {
                const isExpanded = expandedVariations[index];
                const variationSummary = attr.combination?.filter(c => c.value).map(c => c.value).join(" + ") || "New Variation";
                const tempId = attr._tempId || (1000 + index);

                return (
                  <div key={index} className={`mb-24 border radius-12 shadow-sm overflow-hidden transition-all ${isExpanded ? "border-primary-200" : "hover-border-primary-100"}`}>
                    {/* Collapsible Header */}
                    <div
                      className={`p-16 d-flex justify-content-between align-items-center cursor-pointer transition-all ${isExpanded ? "bg-primary-50" : "bg-white"}`}
                      onClick={() => toggleVariation(index)}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div>
                          <h6 className={`text-md mb-0 fw-bold d-flex align-items-center ${isExpanded ? "text-primary-700" : "text-secondary-700"}`}>
                            <Icon icon="ph:package-duotone" className="me-8 text-lg" />
                            #{tempId} {variationSummary}
                          </h6>
                        </div>
                      </div>

                      <div className="d-flex align-items-center gap-4">
                        {/* Preview metrics always shown but styled better */}
                        <div className="d-none d-md-flex gap-12 me-16">
                          <div className="d-flex flex-column align-items-end">
                            <span className="text-xxs text-secondary-400 text-uppercase fw-bold ls-1 mb-1">Price</span>
                            <span className="text-xs fw-bold text-primary-600">₹{attr.price || 0}</span>
                          </div>
                          <div className="vr mx-2 text-secondary-200" style={{ height: "30px" }}></div>
                          <div className="d-flex flex-column align-items-end">
                            <span className="text-xxs text-secondary-400 text-uppercase fw-bold ls-1 mb-1">Stock</span>
                            <span className={`text-xs fw-bold ${attr.stock > 0 ? "text-success-600" : "text-danger-600"}`}>{attr.stock || 0}</span>
                          </div>
                        </div>

                        <div className="d-flex gap-2 ms-12">
                          <button
                            type="button"
                            className="btn btn-sm text-white radius-8 p-8 d-flex align-items-center justify-content-center border-0"
                            style={{ backgroundColor: "#364E3D", width: "32px", height: "32px" }}
                            onClick={(e) => { e.stopPropagation(); toggleVariation(index); }}
                          >
                            <Icon icon="lucide:expand" fontSize="16px" />
                          </button>
                          {!isView && (
                            <button
                              type="button"
                              className="btn btn-sm text-white radius-8 p-8 d-flex align-items-center justify-content-center border-0"
                              style={{ backgroundColor: "#FF4C61", width: "32px", height: "32px" }}
                              onClick={(e) => { e.stopPropagation(); handleRemoveAttribute(index); }}
                            >
                              <Icon icon="fluent:delete-24-regular" fontSize="16px" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Variation Details - Collapsible Content */}
                    <div className={`p-24 bg-white border-top ${isExpanded ? "d-block animate-fadeIn" : "d-none"}`}>
                      <div className="row gy-4">
                        <div className="col-12 col-md-4">
                          <label className="form-label fw-semibold mb-8">SKU <span className="text-danger">*</span></label>
                          <input type="text" className={`form-control radius-8 py-10 ${errors[`variation_${index}_sku`] ? 'is-invalid' : ''}`} value={attr.sku} onChange={(e) => handleAttributeChange(index, "sku", e.target.value)} disabled={isView} placeholder="SKU" />
                          {errors[`variation_${index}_sku`] && <small className="text-danger">{errors[`variation_${index}_sku`]}</small>}
                        </div>
                        <div className="col-12 col-md-4">
                          <label className="form-label fw-semibold mb-8">Regular Price <span className="text-danger">*</span></label>
                          <input type="text" inputMode="decimal" className={`form-control radius-8 py-10 ${errors[`variation_${index}_price`] ? 'is-invalid' : ''}`} value={attr.price} onChange={(e) => handleAttributeChange(index, "price", e.target.value.replace(/[^0-9.]/g, ''))} onKeyDown={handleKeyDown} disabled={isView} placeholder="Regular Price" />
                          {errors[`variation_${index}_price`] && <small className="text-danger">{errors[`variation_${index}_price`]}</small>}
                        </div>
                        <div className="col-12 col-md-4">
                          <label className="form-label fw-semibold mb-8">Stock <span className="text-danger">*</span></label>
                          <input type="text" inputMode="numeric" className={`form-control radius-8 py-10 ${errors[`variation_${index}_stock`] ? 'is-invalid' : ''}`} value={attr.stock} onChange={(e) => handleAttributeChange(index, "stock", e.target.value.replace(/[^0-9]/g, ''))} onKeyDown={handleKeyDown} disabled={isView} placeholder="Stock" />
                          {errors[`variation_${index}_stock`] && <small className="text-danger">{errors[`variation_${index}_stock`]}</small>}
                        </div>

                        {/* <div className="col-12 col-md-6 mt-4">
                          <label className="form-label fw-semibold mb-8">Variation MRP</label>
                          <input type="number" className="form-control radius-8 py-10" value={attr.mrp} onChange={(e) => handleAttributeChange(index, "mrp", e.target.value)} onKeyDown={handleKeyDown} disabled={isView} placeholder="MRP" min="0" />
                        </div> */}

                        {!formData.isSingleImage && (
                          <div className="col-md-12 mt-4">
                            <label className="form-label fw-semibold mb-8">Variation Images (Full Images, Multiple Upload) <span className="text-danger">*</span> <small className="text-secondary-light fw-normal">(Max 2MB)</small></label>
                            <div className={`p-16 border dashed radius-12 bg-light-50 ${errors[`variation_${index}_images`] ? 'border-danger' : ''}`}>
                              {!isView && <input type="file" multiple className="form-control radius-8 mb-16" accept="image/*" onChange={(e) => handleVariationImageUpload(e, index)} disabled={loading} />}
                              <div className="d-flex flex-wrap gap-12">
                                {attr.images && attr.images.map((img, i) => (
                                  <div key={i} className="position-relative border radius-8 bg-white p-2">
                                    <img src={`${IMAGE_BASE_URL}/${img.path || img}`} alt="" className="radius-8" style={{ width: "90px", height: "90px", objectFit: "contain" }} />
                                    {!isView && (
                                      <button type="button" className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 p-1 radius-4" onClick={() => handleRemoveAttributeImage(index, i)}>
                                        <Icon icon="fluent:delete-24-regular" style={{ fontSize: "14px" }} />
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                            {errors[`variation_${index}_images`] && <small className="text-danger mt-8 d-block">{errors[`variation_${index}_images`]}</small>}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {formData.attributes.length === 0 && <div className="text-secondary-light p-32 text-center border dashed radius-12 bg-light-600 fw-medium">No variations added yet. Select attributes above to generate combinations.</div>}
            </div>

            {/* Specifications Section */}
            <div className="mt-5 pt-4 border-top">
              <div className="d-flex align-items-center justify-content-between mb-4">
                <h6 className="mb-0 fw-bold text-lg">Product Specifications</h6>
                {!isView && (
                  <button
                    type="button"
                    className="btn btn-primary-600 radius-8 px-20 py-10 d-flex align-items-center gap-2"
                    onClick={handleAddSpecification}
                    style={{ backgroundColor: "#364e3d", borderColor: "#364e3d" }}
                  >
                    <Icon icon="ic:baseline-plus" fontSize="20px" />
                    <span>Add Specification</span>
                  </button>
                )}
              </div>

              <div className="bg-light-50 p-24 radius-12 border">
                <div className="row gy-4">
                  {formData.specifications.map((spec, index) => (
                    <div key={index} className="col-12 bg-white p-16 radius-8 shadow-sm">
                      <div className="row gy-3 align-items-end">
                        <div className="col-6 col-md-5">
                          <label className="form-label text-xs fw-bold text-secondary-600 mb-8">Property Name</label>
                          <input type="text" className="form-control radius-8 py-10" value={spec.key} onChange={(e) => handleSpecificationChange(index, "key", e.target.value)} disabled={isView} placeholder="e.g. Style, Material, fit, etc." />
                        </div>
                        <div className="col-6 col-md-5">
                          <label className="form-label text-xs fw-bold text-secondary-600 mb-8">Value</label>
                          <input type="text" className="form-control radius-8 py-10" value={spec.value} onChange={(e) => handleSpecificationChange(index, "value", e.target.value)} disabled={isView} placeholder="e.g. Regular, Cotton, Slim, etc." />
                        </div>
                        {!isView && (
                          <div className="col-12 col-md-2 d-flex justify-content-end justify-content-md-start">
                            <button
                              type="button"
                              className="btn btn-danger radius-8 p-10 d-flex align-items-center justify-content-center"
                              onClick={() => handleRemoveSpecification(index)}
                              disabled={formData.specifications.length === 1}
                              title="Remove"
                              style={{ minWidth: "42px", height: "42px", width: "100%" }}
                            >
                              <Icon icon="mingcute:delete-2-line" fontSize="20px" className="me-2 d-md-none" />
                              <span className="d-md-none fw-semibold text-sm">Remove Property</span>
                              <Icon icon="mingcute:delete-2-line" fontSize="20px" className="d-none d-md-inline" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Form Actions for Step 2 */}
            <div className="d-flex justify-content-end gap-3 mt-40 pt-24 border-top">
              <button
                type="button"
                className="btn btn-outline-secondary px-32 py-12 radius-8 fw-semibold"
                style={{ minWidth: "140px" }}
                onClick={() => setCurrentStep(1)}
              >
                Previous
              </button>
              {!isView && hasPermission("Products", id ? "edit" : "add") && (
                <button
                  type="submit"
                  className="btn btn-primary radius-8 px-32 py-12 fw-semibold d-flex align-items-center justify-content-center gap-2"
                  disabled={loading}
                  style={{ backgroundColor: "#364E3D", borderColor: "#364E3D", minWidth: "140px" }}
                >
                  {loading ? (
                    <>
                      <Icon icon="line-md:loading-twotone-loop" fontSize="20px" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Icon icon="tabler:device-floppy" fontSize="20px" />
                      <span>{id ? "Update Product" : "Save Product"}</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </fieldset>

        </form>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={deleteModal.show} onHide={() => setDeleteModal({ show: false, index: null, type: "" })} centered>
        <Modal.Body className="text-center p-40">
          <div className="d-flex justify-content-center mb-16">
            <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
              <Icon icon="fluent:delete-24-regular" className="text-danger-600 text-xxl" />
            </div>
          </div>
          <h5 className="mb-8">Are you sure?</h5>
          <p className="text-secondary-light mb-32 text-md">
            {deleteModal.type === "variation"
              ? "Are you sure you want to remove this variation? All its specific data and images will be detached from this list."
              : "Are you sure you want to remove this specification? This action cannot be undone."}
          </p>
          <div className="d-flex justify-content-center gap-3">
            <Button variant="outline-secondary" className="px-32 py-12 radius-8 fw-semibold" onClick={() => setDeleteModal({ show: false, index: null, type: "" })}>Cancel</Button>
            <Button variant="danger" className="px-32 py-12 radius-8 fw-semibold" onClick={confirmDelete}>Delete</Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Add New Attribute Value Modal */}
      <Modal show={attributeValueModal.show} onHide={() => setAttributeValueModal({ ...attributeValueModal, show: false })} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-md">Add New Attribute Value</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-16">
            <label className="form-label fw-semibold">Value Name</label>
            <input type="text" className="form-control radius-8" placeholder="e.g. Cherry Red" value={attributeValueModal.name} onChange={(e) => setAttributeValueModal(prev => ({ ...prev, name: e.target.value }))} />
          </div>
          <div className="mb-16">
            <label className="form-label fw-semibold">Swatch Image (Crop Enabled) <small className="text-secondary-light fw-normal">(Max 2MB)</small></label>
            <input type="file" className="form-control radius-8" accept="image/*" onChange={handleNewAttributeFileChange} />
            {attributeValueModal.preview && (
              <div className="mt-12 text-center border p-2 radius-8 bg-light">
                <img src={attributeValueModal.preview} alt="Swatch" className="radius-8" style={{ width: "80px", height: "80px", objectFit: "cover" }} />
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" className="btn-sm radius-8 px-24" onClick={() => setAttributeValueModal({ ...attributeValueModal, show: false })}>Cancel</Button>
          <Button variant="primary" className="btn-sm radius-8 px-24" disabled={loading} onClick={saveNewAttributeValue}>{loading ? "Saving..." : "Save Value"}</Button>
        </Modal.Footer>
      </Modal>

      {/* Fly-add Cropper */}
      {cropperState.show && (
        <ImageCropper
          image={cropperState.image}
          onCropComplete={onNewValueCropComplete}
          onCancel={() => setCropperState({ show: false, image: null })}
        />
      )}
    </div>
  );
};

export default ProductFormLayer;

